import React from "react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@shared/ui/accordion";
import {
  FileText,
  DollarSign,
  Link as LinkIcon,
  Receipt,
  Activity,
  AlertTriangle,
} from "lucide-react";
import { cn } from "@shared/lib/utils";
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";

export function ChargesFeesBreakdown({ planConfig }: { planConfig?: any }) {
  const currency = useCurrency();
  const dynamicChargesData = React.useMemo(() => {
    const resData = planConfig?.result || planConfig;
    if (!resData) return [];

    const charges = resData.charges || {};
    const utilityServiceCharges =
      resData.utilityServiceData?.utilityServiceCharges || {};
    const penalties = resData.penalties?.rules || [];

    const mapItem = (item: any) => {
      let amountStr = `${currency} 0`;
      if (item.amount !== null && item.amount !== undefined) {
        amountStr = `${currency} ${item.amount}`;
      } else if (item.fee !== null && item.fee !== undefined) {
        amountStr = `${currency} ${item.fee}`;
      } else if (item.rate !== null && item.rate !== undefined) {
        amountStr = `${item.rate}%`;
      } else if (item.chargeRate && item.chargeRate.length > 0) {
        amountStr = `${currency} ${item.chargeRate[0].rate ?? 0}`;
      }

      let appliesToStr = "N/A";
      if (item.mode) appliesToStr = item.mode.replace(/_/g, " ");
      else if (item.frequency) appliesToStr = item.frequency;
      else if (item.basis) appliesToStr = item.basis;
      else if (item.includeIn || item.include_in)
        appliesToStr = item.includeIn || item.include_in;

      const services = item.utilityServices || item.utility_services;
      return {
        name:
          item.name ||
          item.penaltyName ||
          item.penalty_name ||
          item.code ||
          "N/A",
        amount: amountStr,
        appliesTo: appliesToStr,
        description:
          item.description ||
          (services && services.length > 0
            ? `Applies to: ${services.join(", ")}`
            : "N/A"),
      };
    };

    let fixedItems = (charges.fixedCharges || charges.fixed_charges || []).map(
      mapItem,
    );
    if (
      fixedItems.length === 0 &&
      Object.keys(utilityServiceCharges).length > 0
    ) {
      fixedItems = Object.entries(utilityServiceCharges).map(
        ([name, charge]: [string, any]) => mapItem({ name, ...charge }),
      );
    }

    return [
      {
        id: "fixed",
        title: "Fixed Charges",
        subtitle: "Applied every month regardless of usage",
        icon: DollarSign,
        theme: "blue",
        items: fixedItems,
      },
      {
        id: "linked",
        title: "Linked Charges",
        subtitle: "Tied to consumption / usage readings",
        icon: LinkIcon,
        theme: "purple",
        items: (charges.linkedCharges || charges.linked_charges || []).map(
          mapItem,
        ),
      },
      {
        id: "taxes",
        title: "Taxes & Levies",
        subtitle: "Government-mandated taxes and levies",
        icon: Receipt,
        theme: "gray",
        items: (charges.taxes || []).map(mapItem),
      },
      {
        id: "surcharges",
        title: "Surcharges",
        subtitle: "Program-specific and regulatory surcharges",
        icon: Activity,
        theme: "orange",
        items: (charges.surcharges || []).map(mapItem),
      },
      {
        id: "penalties",
        title: "Penalties & Fees",
        subtitle: "Applied for late payment or service events",
        icon: AlertTriangle,
        theme: "red",
        items: penalties.map(mapItem),
      },
    ].filter((section) => section.items.length > 0);
  }, [planConfig, currency]);

  if (dynamicChargesData.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-gray-400" />
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Charges & Fees Breakdown
        </h3>
        <div className="h-px bg-gray-200 flex-1 ml-2"></div>
      </div>

      <Accordion
        type="single"
        collapsible
        defaultValue="fixed"
        className="space-y-3"
      >
        {dynamicChargesData.map((section) => {
          const Icon = section.icon;
          const themeClasses: Record<string, any> = {
            blue: {
              item: "border-blue-100 [&[data-state=open]]:border-blue-200",
              trigger:
                "bg-blue-50/30 hover:bg-blue-50/60 data-[state=open]:bg-blue-50/50 [&>svg]:text-gray-400",
              icon: "text-blue-600",
              title: "text-gray-900",
              content: "border-blue-100",
            },
            purple: {
              item: "border-purple-100 [&[data-state=open]]:border-purple-200",
              trigger:
                "bg-purple-50/30 hover:bg-purple-50/60 data-[state=open]:bg-purple-50/50 [&>svg]:text-gray-400",
              icon: "text-purple-600",
              title: "text-gray-900",
              content: "border-purple-100",
            },
            gray: {
              item: "border-gray-200 [&[data-state=open]]:border-gray-300",
              trigger:
                "bg-gray-50/30 hover:bg-gray-50/60 data-[state=open]:bg-gray-50/50 [&>svg]:text-gray-400",
              icon: "text-gray-500",
              title: "text-gray-900",
              content: "border-gray-200",
            },
            orange: {
              item: "border-orange-100 [&[data-state=open]]:border-orange-200",
              trigger:
                "bg-orange-50/30 hover:bg-orange-50/60 data-[state=open]:bg-orange-50/50 [&>svg]:text-gray-400",
              icon: "text-orange-500",
              title: "text-gray-900",
              content: "border-orange-100",
            },
            red: {
              item: "border-red-100 [&[data-state=open]]:border-red-200",
              trigger:
                "bg-red-50/30 hover:bg-red-50/60 data-[state=open]:bg-red-50/50 [&>svg]:text-gray-400",
              icon: "text-red-500",
              title: "text-gray-900",
              content: "border-red-100",
            },
          };

          const tc = themeClasses[section.theme] || themeClasses.gray;
          const itemCount = section.items.length;

          return (
            <AccordionItem
              key={section.id}
              value={section.id}
              className={cn(
                "rounded-xl overflow-hidden bg-white shadow-sm transition-colors border",
                tc.item,
              )}
            >
              <AccordionTrigger
                className={cn(
                  "hover:no-underline px-4 py-3 transition-colors",
                  tc.trigger,
                )}
              >
                <div className="flex items-center justify-between w-full pr-3">
                  <div className="flex items-center gap-3">
                    <Icon className={cn("w-4 h-4 shrink-0", tc.icon)} />
                    <div className="text-left">
                      <p className={cn("font-semibold text-sm", tc.title)}>
                        {section.title}
                      </p>
                      <p className="text-xs text-gray-500 font-medium">
                        {section.subtitle}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 font-medium">
                    {itemCount} items
                  </p>
                </div>
              </AccordionTrigger>
              <AccordionContent className={cn("p-0 border-t", tc.content)}>
                {section.items.length > 0 ? (
                  <div className="overflow-x-auto border-t-0">
                    <GenericTable
                      data={section.items || []}
                      columns={[
                        {
                          key: "name",
                          header: "Charge",
                          type: "custom",
                          render: (value: any) => (
                            <span className="font-semibold text-xs text-gray-900">
                              {value}
                            </span>
                          ),
                        },
                        {
                          key: "amount",
                          header: "Amount / Rate",
                          type: "custom",
                          render: (value: any, row: any) => (
                            <span className="font-semibold text-xs text-gray-900">
                              {value}
                              <span className="text-xs text-gray-400 font-medium">
                                {row.frequency}
                              </span>
                            </span>
                          ),
                        },
                        {
                          key: "appliesTo",
                          header: "Applies To",
                          type: "custom",
                          render: (value: any) => (
                            <span className="text-xs text-gray-500 font-medium">
                              {value}
                            </span>
                          ),
                        },
                      ]}
                      rowKey="name"
                    />
                  </div>
                ) : (
                  <div className="p-4 text-xs text-gray-500 italic text-center">
                    No active charges in this category
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}
